import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useCampaign } from '../hooks/useCampaigns';
import { campaignApi } from '../services/api';
import CampaignForm from '../components/campaigns/CampaignForm';
import type { CampaignFormData } from '../types/campaign';

const CampaignEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { campaign, loading, error, refreshCampaign } = useCampaign(id || '');

  const handleSubmit = async (data: CampaignFormData) => {
    if (!id) return;
    
    try {
      await campaignApi.update(id, data);
      navigate(`/campaigns/${id}`);
    } catch (error) {
      console.error('Failed to update campaign:', error);
      throw error;
    }
  };

  const handleCancel = () => {
    navigate(`/campaigns/${id}`);
  };

  // Convert Campaign to CampaignFormData for editing
  const convertCampaignToFormData = (campaign: any): Partial<CampaignFormData> => {
    if (!campaign) return {};

    return {
      name: campaign.name,
      description: campaign.description,
      campaignType: campaign.campaignType,
      status: campaign.status,
      startDate: campaign.startDate ? new Date(campaign.startDate).toISOString().split('T')[0] : undefined,
      endDate: campaign.endDate ? new Date(campaign.endDate).toISOString().split('T')[0] : undefined,
      budget: campaign.budget ? parseFloat(campaign.budget) : undefined,
      currency: campaign.currency,
      targetingSettings: campaign.targetingSettings || {},
      frequencyCap: campaign.frequencyCap || {},
      autoScheduling: campaign.autoScheduling || {},
      validationRules: campaign.validationRules || {},
      enhancedFeatures: campaign.enhancedFeatures || {},
      targetKeywords: campaign.targetKeywords?.map((kw: any) => kw.keyword || kw) || [],
      adCreatives: campaign.adCreatives || [],
      impressionLimits: campaign.impressionLimits || {},
      negativeKeywords: campaign.targeting?.negativeKeywords?.map((kw: any) => kw.keyword || kw) || [],
      negativeBrands: campaign.targeting?.negativeBrands || [],
      sceneTargeting: campaign.targeting?.sceneTargeting || [],
      contentTypeTargeting: campaign.targeting?.contentTypeTargeting || [],
      specificContentTargeting: campaign.targeting?.specificContentTargeting || []
    };
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="space-y-4">
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
            <div className="h-10 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Kampanya yüklenirken hata oluştu
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <div className="flex space-x-3">
                  <button
                    onClick={() => refreshCampaign()}
                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                  >
                    Tekrar Dene
                  </button>
                  <button
                    onClick={() => navigate('/campaigns')}
                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                  >
                    Kampanyalara Dön
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h3 className="mt-2 text-sm font-medium text-gray-900">Kampanya bulunamadı</h3>
          <p className="mt-1 text-sm text-gray-500">
            Düzenlemek istediğiniz kampanya bulunamadı.
          </p>
          <div className="mt-6">
            <button
              onClick={() => navigate('/campaigns')}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Kampanyalara Dön
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate(`/campaigns/${id}`)}
            className="mr-4 p-2 rounded-md text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">
              Kampanya Düzenle
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              {campaign.name} kampanyasını düzenleyin
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <CampaignForm
        initialData={convertCampaignToFormData(campaign)}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        mode="edit"
      />
    </div>
  );
};

export default CampaignEdit; 